/**
 * RTK Query API service for screening configuration.
 *
 * Provides endpoints for retrieving screening questions and
 * qualification rules based on case type.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CaseTypeScreeningConfig,
  ScreeningConfigByCaseTypeParams,
  ScreeningConfigListItem,
  ScreeningConfigListParams,
} from '../types/screening-config-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const screeningConfigApiClient = createApi({
  reducerPath: 'screeningConfigApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['ScreeningConfig'] as const,
  endpoints: (builder) => ({
    /**
     * List all available screening configurations.
     * Supports filtering by case type, active status, and firm.
     */
    listScreeningConfigs: builder.query<
      ScreeningConfigListItem[],
      ScreeningConfigListParams | void
    >({
      query: (params = {}) => ({
        url: 'api/screening-configs/',
        params: {
          ...params,
          is_active:
            typeof params?.is_active === 'boolean'
              ? String(params.is_active)
              : undefined,
        },
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({ id }) => ({
              type: 'ScreeningConfig' as const,
              id,
            })),
            { type: 'ScreeningConfig', id: 'LIST' },
          ];
        }
        return [{ type: 'ScreeningConfig', id: 'LIST' }];
      },
    }),

    /**
     * Get a specific screening configuration by ID.
     * Returns the full configuration including categories, questions, and rules.
     */
    getScreeningConfig: builder.query<CaseTypeScreeningConfig, string>({
      query: (id) => `api/screening-configs/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ScreeningConfig', id }],
    }),

    /**
     * Get screening configuration for a specific case type.
     *
     * This is the primary endpoint used by the frontend to load screening
     * questions for an intake. It automatically resolves firm-specific
     * overrides and falls back to defaults.
     *
     * Resolution order:
     * 1. Firm-specific config for case type
     * 2. Global config for case type
     * 3. Default config (is_default=True)
     */
    getScreeningConfigByCaseType: builder.query<
      CaseTypeScreeningConfig,
      ScreeningConfigByCaseTypeParams
    >({
      query: (params) => {
        // Filter out undefined/null/empty values to ensure clean URL params
        const cleanParams: Record<string, string> = {
          case_type: params.case_type,
        };
        if (params.firm_id) {
          cleanParams.firm_id = params.firm_id;
        }
        return {
          url: 'api/screening-configs/by-case-type/',
          params: cleanParams,
        };
      },
      transformResponse: (response: CaseTypeScreeningConfig) => {
        return response;
      },
      providesTags: (result, error, params) => [
        {
          type: 'ScreeningConfig',
          id: `case_type:${params.case_type}:firm:${params.firm_id ?? 'global'}`,
        },
        ...(result
          ? [{ type: 'ScreeningConfig' as const, id: result.id }]
          : []),
      ],
    }),
  }),
});

export const {
  useListScreeningConfigsQuery,
  useGetScreeningConfigQuery,
  useGetScreeningConfigByCaseTypeQuery,
} = screeningConfigApiClient;
